// The two-press gate of every control that destroys something
// (ui-map-interface.md §12, R696). The first press arms one key and the
// control shows its armed face, which names the act (R2069); a second
// press on the same key inside the window fires. A press on another key
// moves the arming to that key, so one armed row can never fire a
// different control. The window is
// `chrome.arm_ms`, and the host passes it in with its own redraw.
//
// The gate holds no DOM and plays no sound: the host draws the label with
// `label()` and puts an `armed` class on the button, because a rebuilt
// button loses `:hover` and a finger never had it.

export class ArmGate {
  private armed = '';
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly ms: number,
    private readonly onChange: () => void,
  ) {}

  /**
   * The press. Returns true on the second press of the armed key, and the
   * gate is at rest again; returns false after it armed `key`, restarted
   * the window and asked the host to redraw.
   */
  press(key: string): boolean {
    if (this.armed === key) {
      this.clear(false);
      return true;
    }
    this.armed = key;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.clear(true), this.ms);
    this.onChange();
    return false;
  }

  /** True while `key` is the armed key. */
  is(key: string): boolean {
    return this.armed !== '' && this.armed === key;
  }

  /** The armed key, or '' at rest. A pure markup function takes it. */
  get key(): string {
    return this.armed;
  }

  /** True while any key is armed. */
  get active(): boolean {
    return this.armed !== '';
  }

  /**
   * Puts the gate back at rest. Costs nothing when nothing is armed. The
   * host passes `redraw = false` from inside a render pass or when the
   * page is about to leave.
   */
  clear(redraw = true): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = undefined; }
    if (this.armed === '') return;
    this.armed = '';
    if (redraw) this.onChange();
  }

  /** The label a control wears: `armed` while `key` is armed, else `rest`. */
  label(key: string, rest: string, armed: string): string {
    return this.is(key) ? armed : rest;
  }
}
